import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { applyTheme } from "../theme";
import { useAuth, AUTH_ME_QUERY_KEY, type User } from "../hooks/useAuth";

export function UserSettingsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [theme, setTheme] = useState<string>("dark");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.theme) setTheme(user.theme);
  }, [user?.theme]);

  async function handleSave() {
    setSaving(true);
    setError("");
    setSaveSuccess(false);

    try {
      const updated = await apiFetch<{ user: User }>("/api/auth/me", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ theme }),
      });
      applyTheme(theme as "dark" | "light");
      queryClient.setQueryData(AUTH_ME_QUERY_KEY, updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }

  function handleThemeChange(newTheme: string) {
    setTheme(newTheme);
    applyTheme(newTheme);
  }

  return (
    <div className="general-settings">
      <div className="general-settings-form-panel">
        <div className="general-settings-form-inner">
          <div className="general-settings-page-header">
            <h1>Settings</h1>
            <p className="page-subtitle">Manage your account preferences</p>
          </div>

          <div className="general-settings-form">
            <section className="settings-section">
              <h4 className="section-header">
                <span className="material-symbols-outlined">palette</span>
                Appearance
              </h4>
              <div className="identity-fields">
                <div>
                  <label>Theme</label>
                  <select value={theme} onChange={(e) => handleThemeChange(e.target.value)}>
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                  <p className="form-tip">Choose your preferred color theme for the app interface.</p>
                </div>
                <div>
                  <button
                    type="button"
                    className={`btn btn-primary ${saveSuccess ? "btn-saved" : ""}`}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : saveSuccess ? "Saved!" : "Save Changes"}
                  </button>
                </div>
              </div>
            </section>
          </div>

          {error && <div className="auth-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}
